/**
 * Utility functions for US phone number processing and formatting
 */

/**
 * Regular expression for US phone number validation
 * Matches various formats including:
 * - (555) 123-4567
 * - 555-123-4567
 * - 555.123.4567
 * - +1 (555) 123-4567
 * - +1 555-123-4567
 * - 5551234567
 */
export const US_PHONE_REGEX =
  /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})$/;

/**
 * Processes pasted phone number text and formats it with +1 prefix
 *
 * Handles various input formats and returns a consistently formatted value:
 * - Plain digits: "5551234567" -> "5551234567"
 * - With +1 prefix: "+1 5551234567", "+15551234567" -> "5551234567"
 * - Formatted: "(555) 123-4567", "555-123-4567" -> "5551234567"
 * - Complex: "+1 (555) 123-4567" -> "5551234567"
 * - Extra digits: "156189561452" -> "5618956145" (takes first 10 digits after removing leading 1)
 *
 * Note: Returns 10-digit format without +1 for display in masked input fields.
 * Use formatPhoneNumberForSubmit() to add +1 before API submission.
 *
 * @param pastedText - The text pasted from clipboard
 * @returns Clean 10-digit phone number or null if invalid
 *
 * @example
 * processPhoneNumberPaste('+1 (555) 123-4567') // Returns '5551234567'
 * processPhoneNumberPaste('5551234567') // Returns '5551234567'
 * processPhoneNumberPaste('+15551234567') // Returns '5551234567'
 * processPhoneNumberPaste('156189561452') // Returns '5618956145' (first 10 after removing 1)
 * processPhoneNumberPaste('123') // Returns null (invalid)
 */
export function processPhoneNumberPaste(pastedText: string): string | null {
  if (!pastedText) {
    return null;
  }

  let value = pastedText;

  // Remove any existing +1 prefix to avoid duplication
  value = value.replace(/^\+1\s*/, '');

  // Try to extract digits using the regex first (for formatted numbers)
  const regexMatch = value.replace(US_PHONE_REGEX, `$1$2$3$4`);

  // If regex didn't change anything, extract only digits (for plain number strings)
  if (regexMatch === value) {
    // Extract only digits
    const digitsOnly = value.replace(/\D/g, '');

    // Case 1: Exactly 10 digits - valid US number
    if (digitsOnly.length === 10) {
      return digitsOnly;
    }

    // Case 2: 11+ digits - check if it starts with 1, then take next 10 digits
    if (digitsOnly.length >= 11) {
      if (digitsOnly.startsWith('1')) {
        // Remove leading 1 and take first 10 digits
        const withoutLeading1 = digitsOnly.substring(1);
        if (withoutLeading1.length >= 10) {
          const first10 = withoutLeading1.substring(0, 10);
          // Validate that it's a valid US number (starts with 2-9)
          if (/^[2-9]/.test(first10)) {
            return first10;
          }
        }
      } else {
        // Doesn't start with 1, take first 10 digits
        const first10 = digitsOnly.substring(0, 10);
        // Validate that it's a valid US number (starts with 2-9)
        if (/^[2-9]/.test(first10)) {
          return first10;
        }
      }
    }

    // If not 10 digits and doesn't match above cases, return null (invalid input)
    return null;
  }

  return regexMatch;
}

/**
 * Formats a US phone number for submission by adding the +1 country code
 *
 * Handles various US phone number formats:
 * - Plain 10 digits: "5551234567" -> "+15551234567"
 * - Formatted: "(555) 123-4567" -> "+15551234567"
 * - Already with +1: "+15551234567" -> "+15551234567" (unchanged)
 * - 11 digits with 1: "15551234567" -> "+15551234567"
 *
 * @param phoneNumber - The phone number to format
 * @returns Phone number with +1 country code prefix
 *
 * @example
 * formatPhoneNumberForSubmit('5551234567') // Returns '+15551234567'
 * formatPhoneNumberForSubmit('(555) 123-4567') // Returns '+15551234567'
 * formatPhoneNumberForSubmit('+15551234567') // Returns '+15551234567'
 * formatPhoneNumberForSubmit('invalid') // Returns 'invalid'
 */
export function formatPhoneNumberForSubmit(phoneNumber: string): string {
  if (!phoneNumber) {
    return phoneNumber;
  }

  // If already has +1 prefix, return as is (already formatted)
  if (phoneNumber.trim().startsWith('+1')) {
    return phoneNumber.trim();
  }

  const digitsOnly = phoneNumber.replace(/\D/g, '');

  // Case 1: Exactly 10 digits - US number, add +1
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }

  // Case 2: Exactly 11 digits starting with 1 - US number with country code, add +
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `+${digitsOnly}`;
  }

  // Case 3: Try US regex for formatted numbers like (555) 123-4567
  const regexMatch = phoneNumber.replace(US_PHONE_REGEX, '+1$1$2$3$4');
  if (regexMatch !== phoneNumber) {
    return regexMatch;
  }

  // Return original if none of the above matched (invalid format)
  return phoneNumber;
}

/**
 * Handles the paste event for phone number inputs
 * Prevents default paste behavior and sets the processed value
 *
 * @param event - The ClipboardEvent from paste
 * @param setValue - Callback function to set the processed value
 *
 * @example
 * // In a component:
 * onPaste(event: ClipboardEvent) {
 *   handlePhoneNumberPaste(event, (value) => {
 *     if (value) {
 *       this.phoneControl.setValue(value);
 *     }
 *   });
 * }
 */
export function handlePhoneNumberPaste(
  event: ClipboardEvent,
  setValue: (value: string) => void,
): void {
  event.preventDefault();
  const clipboardData = event.clipboardData;
  const pastedText = clipboardData?.getData('text');

  if (pastedText) {
    const processedValue = processPhoneNumberPaste(pastedText);
    if (processedValue) {
      setValue(processedValue);
    }
  }
}

/**
 * Extracts only digits from a phone number string
 *
 * @param phoneNumber - The phone number string
 * @returns String containing only digits
 *
 * @example
 * extractDigits('(555) 123-4567') // Returns '5551234567'
 */
export function extractDigits(phoneNumber: string): string {
  return phoneNumber.replace(/\D/g, '');
}

/**
 * Validates if a string is a valid US phone number
 * Accepts 10-digit format, numbers with +1 country code prefix, and numbers with extra digits
 *
 * @param phoneNumber - The phone number to validate
 * @returns true if valid US phone number, false otherwise
 *
 * @example
 * isValidUSPhoneNumber('5551234567') // Returns true (US 10 digits)
 * isValidUSPhoneNumber('+15551234567') // Returns true (US with +1)
 * isValidUSPhoneNumber('(555) 123-4567') // Returns true (US formatted)
 * isValidUSPhoneNumber('156189561452') // Returns true (12 digits, validates first 10 after removing 1)
 * isValidUSPhoneNumber('123') // Returns false (too short)
 */
export function isValidUSPhoneNumber(phoneNumber: string): boolean {
  if (!phoneNumber) {
    return false;
  }

  const digits = extractDigits(phoneNumber);

  // Case 1: Exactly 10 digits (US format without country code)
  // Example: 5551234567, (555) 123-4567
  if (digits.length === 10) {
    return /^[2-9]/.test(digits); // Must start with 2-9 for US area codes
  }

  // Case 2: 11+ digits
  if (digits.length >= 11) {
    // If starts with 1, remove it and validate the next 10 digits
    if (digits.startsWith('1')) {
      const withoutLeading1 = digits.substring(1);
      if (withoutLeading1.length >= 10) {
        const first10 = withoutLeading1.substring(0, 10);
        // Must start with 2-9 for valid US area code
        return /^[2-9]/.test(first10);
      }
    } else {
      // Doesn't start with 1, validate first 10 digits
      const first10 = digits.substring(0, 10);
      // Must start with 2-9 for valid US area code
      return /^[2-9]/.test(first10);
    }
  }

  return false;
}
